// The customer's NIF on the case page.
//
// When the lawyer uploads the Finanças confirmation, the backend reads the
// number off it. A reading it trusted is already on the case and only needs a
// glance; a reading it did not trust is shown with the reason, and the lawyer
// either takes it over or types the number from the document. Whatever is
// stored here is what the customer sees in their account, so every save goes
// through the check digit, once here and once on the backend.

import React, { useEffect, useState } from 'react';
import { NifExtraction, formatNif, isValidNif } from '../../api/nifNumber';

export interface NifNumberPanelProps {
  nifNumber?: string | null;
  source?: 'extracted' | 'lawyer' | null;
  updatedBy?: string | null;
  extraction?: NifExtraction | null;
  /** True while the backend is still reading a document that was just uploaded. */
  reading?: boolean;
  /** Only shown for NIF cases; a certificate has no number to record. */
  onSave: (nifNumber: string) => Promise<void>;
}

const describeReading = (extraction: NifExtraction): string => {
  const from = extraction.file_name ? ` from ${extraction.file_name}` : '';
  switch (extraction.status) {
    case 'extracted':
      return `Read${from}.`;
    case 'review':
      return `Read ${formatNif(extraction.nif)}${from}, but ${lowerFirst(extraction.reason)} Check it against the document.`;
    case 'no_nif':
      return `No NIF found${from}. Enter it from the document.`;
    case 'failed':
    default:
      return `The document${from} could not be read automatically. Enter the NIF from the document.`;
  }
};

const lowerFirst = (text: string | undefined): string => {
  const line = text?.trim() || 'it could not be trusted on its own.';
  return line.charAt(0).toLowerCase() + line.slice(1);
};

const NifNumberPanel: React.FC<NifNumberPanelProps> = ({
  nifNumber,
  source,
  updatedBy,
  extraction,
  reading = false,
  onSave,
}) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const stored = (nifNumber ?? '').trim();
  const needsReview = Boolean(extraction?.review_needed);

  // A fresh extraction result closes an editor that was opened on the old one.
  useEffect(() => {
    setEditing(false);
    setError('');
  }, [extraction?.document_id, extraction?.status]);

  const startEditing = (prefill: string) => {
    setDraft(formatNif(prefill));
    setError('');
    setEditing(true);
  };

  const save = async (value: string) => {
    const digits = value.replace(/\D+/g, '');
    if (digits && !isValidNif(digits)) {
      setError('A NIF is nine digits with a valid check digit. Check the number against the document.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await onSave(digits);
      setEditing(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save the NIF.');
    } finally {
      setSaving(false);
    }
  };

  const editor = (
    <div className="nif-panel-editor">
      <input
        type="text"
        inputMode="numeric"
        className="financas-reference-input nif-panel-input"
        aria-label="NIF"
        placeholder="293 471 606"
        maxLength={11}
        value={draft}
        onChange={event => setDraft(event.target.value)}
        onKeyDown={event => {
          if (event.key === 'Enter') save(draft);
          if (event.key === 'Escape') setEditing(false);
        }}
        disabled={saving}
        autoFocus
      />
      <button type="button" className="financas-btn financas-btn--primary" onClick={() => save(draft)} disabled={saving}>
        {saving ? 'Saving…' : 'Save NIF'}
      </button>
      <button
        type="button"
        className="financas-btn financas-btn--link"
        onClick={() => { setEditing(false); setError(''); }}
        disabled={saving}
      >
        Cancel
      </button>
    </div>
  );

  let tone: 'reading' | 'ok' | 'confirm' | 'review' | 'empty' = 'empty';
  if (reading) tone = 'reading';
  else if (stored && source === 'lawyer') tone = 'ok';
  else if (stored) tone = 'confirm';
  else if (needsReview) tone = 'review';

  return (
    <div className={`nif-panel nif-panel--${tone}`} data-testid="nif-panel">
      <div className="nif-panel-head">
        <span className="info-item-label">Customer's NIF</span>
        {tone === 'reading' && (
          <span className="nif-panel-status" role="status">Reading the NIF off the uploaded document…</span>
        )}
      </div>

      {tone !== 'reading' && (
        <>
          <div className="nif-panel-number" data-testid="case-nif-number">
            {stored ? formatNif(stored) : 'Not recorded'}
          </div>
          <p className="nif-panel-note">
            {tone === 'ok' && `Confirmed by ${updatedBy || 'a lawyer'}. This is the number the customer sees in their account.`}
            {tone === 'confirm' && extraction && `${describeReading(extraction)} The customer sees this number in their account; confirm it against the document, or correct it.`}
            {tone === 'confirm' && !extraction && 'The customer sees this number in their account; confirm it against the document, or correct it.'}
            {tone === 'review' && extraction && describeReading(extraction)}
            {tone === 'empty' && 'Enter the number from the Finanças confirmation so the customer sees it in their account.'}
          </p>

          {editing ? (
            editor
          ) : (
            <div className="nif-panel-actions">
              {tone === 'confirm' && (
                <>
                  <button type="button" className="financas-btn financas-btn--primary" onClick={() => save(stored)} disabled={saving}>
                    {saving ? 'Saving…' : 'Confirm NIF'}
                  </button>
                  <button type="button" className="financas-btn financas-btn--link" onClick={() => startEditing(stored)}>
                    Correct
                  </button>
                </>
              )}
              {tone === 'review' && extraction?.nif && (
                <button type="button" className="financas-btn financas-btn--link" onClick={() => startEditing(extraction.nif)}>
                  Use {formatNif(extraction.nif)}
                </button>
              )}
              {(tone === 'review' || tone === 'empty') && (
                <button type="button" className="financas-btn financas-btn--link" onClick={() => startEditing('')}>
                  Enter NIF
                </button>
              )}
              {tone === 'ok' && (
                <button type="button" className="financas-btn financas-btn--link" onClick={() => startEditing(stored)}>
                  Change
                </button>
              )}
            </div>
          )}
          {error && <span className="financas-office-error" role="alert" data-testid="nif-panel-error">{error}</span>}
        </>
      )}
    </div>
  );
};

export default NifNumberPanel;
